import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  Mail,
  MapPin,
  Send,
  Sparkles,
  Truck,
  User,
} from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'

/**
 * Mission detail — strict read-only view.
 *
 * Reachable from `/missions/:id`. The driver can NOT edit a
 * filed mission from here; once a "bon" is in, the only allowed
 * mutation is for the admin to flip `verified` / `readyToSend`.
 * If a driver realises they made a mistake, they file a new
 * mission — the V1 had the same policy.
 *
 * The page splits into five sections that mirror the mission
 * form so the driver gets a familiar mental model:
 *   1. Identity (site, date, client, vehicle)
 *   2. Time slots (raw HH:MM, with the rounded value the API
 *      stored)
 *   3. Totals (computed by the engine at submission time, fetched
 *      back as integers in minutes — formatted as `XhYY` here)
 *   4. Bonus (lavage, déplacement, péage)
 *   5. Signatures + photo (rendered from R2 URLs the API hands
 *      back)
 *
 * Status badges (verified / ready / draft) sit at the top to
 * answer "is this bon already paid?" in one glance.
 */

interface MissionDetail {
  id: string
  driverId: string
  driverName: string | null
  vehicleId: string | null
  vehicleRegistration: string | null
  clientId: string | null
  clientName: string | null
  planningId: string | null
  templateId: string | null

  site: string
  date: string

  morningStart: string | null
  morningEnd: string | null
  afternoonStart: string | null
  afternoonEnd: string | null

  totalMinutes: number
  dayMinutes: number
  nightMinutes: number
  washingMinutes: number
  travelMinutes: number
  bonusMinutes: number
  totalHours: string
  dayHours: string
  nightHours: string

  washing: boolean
  travel: string

  supervisorSignatureUrl: string | null
  driverSignatureUrl: string | null
  supervisorEmail: string | null
  receiptPhotoUrl: string | null
  tollAmount: string | null

  readyToSend: boolean
  verified: boolean
  isDaily: boolean

  createdAt: string
  updatedAt: string
}

export function MissionDetailPage() {
  const api = useApi()
  const navigate = useNavigate()
  // `strict: false` lets us read the `id` param without coupling
  // to the protected-layout id ('authed') in the type-resolved
  // path. The cast keeps it typed.
  const params = useParams({ strict: false }) as { id?: string }
  const id = params.id ?? ''

  const detailQuery = useQuery({
    queryKey: ['missions', 'detail', id] as const,
    queryFn: () => api.get<MissionDetail>(`/api/missions/${id}`),
    staleTime: 60 * 1000,
  })

  return (
    <AppShell>
      <button
        type="button"
        onClick={() => void navigate({ to: '/missions/history' })}
        className={[
          'inline-flex items-center gap-2 mb-3',
          'min-h-[2.5rem] px-2 -ml-2 rounded-lg',
          'text-text-muted hover:text-text hover:bg-surface-muted',
          'transition-colors duration-100',
        ].join(' ')}
      >
        <ArrowLeft size={18} aria-hidden="true" />
        Retour à l'historique
      </button>

      {detailQuery.isLoading ? (
        <div className="py-12 flex justify-center">
          <Spinner size={28} label="Chargement de la mission" />
        </div>
      ) : detailQuery.isError ? (
        <Card className="p-4 text-danger">
          Mission introuvable. Elle a peut-être été supprimée par un admin.
        </Card>
      ) : detailQuery.data !== undefined ? (
        <MissionDetailBody mission={detailQuery.data} />
      ) : null}
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Body — split out so it's easy to add a print / share variant later         */
/* -------------------------------------------------------------------------- */

function MissionDetailBody({ mission }: { mission: MissionDetail }) {
  return (
    <div className="flex flex-col gap-4 pb-4">
      {/* Title + status */}
      <header>
        <h1 className="text-2xl font-bold text-text">{mission.site}</h1>
        <p className="text-sm text-text-muted mt-1">{formatHeaderDate(mission.date)}</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {mission.verified ? (
            <Badge tone="success">
              <CheckCircle2 size={12} aria-hidden="true" />
              Vérifié
            </Badge>
          ) : mission.readyToSend ? (
            <Badge tone="info">
              <Send size={12} aria-hidden="true" />
              Prêt à envoyer
            </Badge>
          ) : (
            <Badge tone="neutral">
              <ClipboardCheck size={12} aria-hidden="true" />
              Brouillon
            </Badge>
          )}
          {mission.isDaily ? <Badge tone="template">Journalier</Badge> : null}
        </div>
      </header>

      {/* 1. Identity */}
      <Section title="Infos mission">
        <KeyValue
          icon={<MapPin size={16} />}
          label="Chantier"
          value={mission.site}
        />
        <KeyValue
          icon={<Calendar size={16} />}
          label="Date"
          value={formatDate(mission.date)}
        />
        <KeyValue
          icon={<User size={16} />}
          label="Client"
          value={mission.clientName ?? '—'}
        />
        <KeyValue
          icon={<Truck size={16} />}
          label="Véhicule"
          value={mission.vehicleRegistration ?? '—'}
        />
      </Section>

      {/* 2. Time slots */}
      <Section title="Créneaux">
        <SlotLine label="Matin" start={mission.morningStart} end={mission.morningEnd} />
        <SlotLine label="Après-midi" start={mission.afternoonStart} end={mission.afternoonEnd} />
      </Section>

      {/* 3. Totals */}
      <Section title="Totaux">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Total" value={formatMinutes(mission.totalMinutes)} primary />
          <Stat label="Jour" value={formatMinutes(mission.dayMinutes)} />
          <Stat label="Nuit" value={formatMinutes(mission.nightMinutes)} />
          {mission.bonusMinutes > 0 ? (
            <Stat
              label="Bonus"
              value={`+${formatMinutes(mission.bonusMinutes)}`}
            />
          ) : null}
        </div>
      </Section>

      {/* 4. Bonus / extras */}
      {(mission.washing || mission.travel !== '' || mission.tollAmount !== null) ? (
        <Section title="Bonus & extras">
          {mission.travel !== '' ? (
            <KeyValue
              icon={<Sparkles size={16} />}
              label="Déplacement"
              value={mission.travel}
            />
          ) : null}
          {mission.washing ? (
            <KeyValue
              icon={<Sparkles size={16} />}
              label="Lavage"
              value="Oui (+30 min)"
            />
          ) : null}
          {mission.tollAmount !== null ? (
            <KeyValue
              icon={<Sparkles size={16} />}
              label="Péage / OBU"
              value={`${mission.tollAmount} €`}
            />
          ) : null}
        </Section>
      ) : null}

      {/* 5. Signatures + photo */}
      {(mission.supervisorSignatureUrl !== null ||
        mission.driverSignatureUrl !== null ||
        mission.receiptPhotoUrl !== null ||
        mission.supervisorEmail !== null) ? (
        <Section title="Signatures & bon">
          {mission.supervisorEmail !== null ? (
            <KeyValue
              icon={<Mail size={16} />}
              label="Email chef de chantier"
              value={mission.supervisorEmail}
            />
          ) : null}
          {mission.supervisorSignatureUrl !== null ? (
            <SignatureImage
              label="Signature chef de chantier"
              url={mission.supervisorSignatureUrl}
            />
          ) : null}
          {mission.driverSignatureUrl !== null ? (
            <SignatureImage
              label="Signature chauffeur"
              url={mission.driverSignatureUrl}
            />
          ) : null}
          {mission.receiptPhotoUrl !== null ? (
            <ReceiptImage url={mission.receiptPhotoUrl} />
          ) : null}
        </Section>
      ) : null}

      {/* Footer — audit timestamps */}
      <p className="text-xs text-text-muted pt-2 border-t border-surface-border">
        Mission créée le {formatDateTime(mission.createdAt)} · dernière
        modification le {formatDateTime(mission.updatedAt)}.
      </p>

      <Button
        type="button"
        variant="ghost"
        size="md"
        onClick={() => window.history.back()}
      >
        <ArrowLeft size={18} />
        Retour
      </Button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Building blocks                                                            */
/* -------------------------------------------------------------------------- */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-4 flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
        {title}
      </h2>
      <div className="flex flex-col gap-2">{children}</div>
    </Card>
  )
}

function KeyValue({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-text-muted mt-0.5 shrink-0" aria-hidden="true">
        {icon}
      </span>
      <div className="flex flex-col min-w-0">
        <span className="text-xs text-text-muted">{label}</span>
        <span className="text-base text-text truncate">{value}</span>
      </div>
    </div>
  )
}

function SlotLine({
  label,
  start,
  end,
}: {
  label: string
  start: string | null
  end: string | null
}) {
  const filled = start !== null && end !== null
  return (
    <div className="flex items-center justify-between min-h-[2.5rem]">
      <span className="text-sm font-medium text-text-muted">{label}</span>
      {filled ? (
        <span className="font-mono tabular-nums text-base">
          {trimSeconds(start)} → {trimSeconds(end)}
        </span>
      ) : (
        <span className="text-text-muted">—</span>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  primary,
}: {
  label: string
  value: string
  primary?: boolean
}) {
  return (
    <div
      className={[
        'rounded-xl p-3',
        primary ? 'bg-info-soft text-info' : 'bg-surface-muted text-text',
      ].join(' ')}
    >
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p
        className={[
          'font-mono tabular-nums',
          primary ? 'text-2xl font-bold' : 'text-xl font-semibold',
        ].join(' ')}
      >
        {value}
      </p>
    </div>
  )
}

function SignatureImage({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-text-muted">{label}</span>
      <img
        src={url}
        alt={label}
        className="w-full max-h-[200px] object-contain bg-surface border border-surface-border rounded-lg"
      />
    </div>
  )
}

function ReceiptImage({ url }: { url: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-text-muted">Photo du bon</span>
      <a href={url} target="_blank" rel="noopener noreferrer" aria-label="Ouvrir la photo en grand">
        <img
          src={url}
          alt="Bon de livraison"
          className="w-full max-h-[400px] object-contain bg-surface-muted border border-surface-border rounded-lg"
        />
      </a>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Formatting helpers                                                         */
/* -------------------------------------------------------------------------- */

function formatMinutes(total: number): string {
  if (total <= 0) return '0h00'
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${hours}h${String(minutes).padStart(2, '0')}`
}

function trimSeconds(value: string): string {
  // The API stores HH:MM:SS for slots. Show HH:MM in the UI.
  return value.length === 8 ? value.slice(0, 5) : value
}

function formatHeaderDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
